import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Icon, ICONS } from '@/lib/icons'
import type { KnockoutBracket, KnockoutMatch, Player } from '@/types/api'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getRoundLabel } from './utils'

interface CurrentBracketStandingsProps {
  bracket: KnockoutBracket
  playersById: Record<string, Player>
}

type UpcomingMatch = {
  match: KnockoutMatch
  roundLabel: string
  playerAName?: string
  playerBName?: string
  ready: boolean
}

type WaitingMatch = {
  match: KnockoutMatch
  roundLabel: string
  missingSide: 'A' | 'B' | 'BOTH'
}

export function CurrentBracketStandings({ bracket, playersById }: CurrentBracketStandingsProps) {
  const { t } = useTranslation()

  const { completedMatches, totalMatches, readyMatches, waitingMatches } = useMemo(() => {
    const completed = bracket.matches.filter(match => match.completed).length
    const total = bracket.matches.length

    const ready: UpcomingMatch[] = []
    const waiting: WaitingMatch[] = []

    for (const match of bracket.matches) {
      if (match.completed) {
        continue
      }

      const roundLabel = getRoundLabel(match.roundNumber, bracket.totalRounds, t)
      const playerAName = match.playerAId ? playersById[match.playerAId]?.displayName : undefined
      const playerBName = match.playerBId ? playersById[match.playerBId]?.displayName : undefined

      if (match.playerAId && match.playerBId) {
        ready.push({
          match,
          roundLabel,
          playerAName,
          playerBName,
          ready: true
        })
      } else {
        const missingSide: WaitingMatch['missingSide'] = !match.playerAId && !match.playerBId
          ? 'BOTH'
          : !match.playerAId
            ? 'A'
            : 'B'

        waiting.push({
          match,
          roundLabel,
          missingSide
        })
      }
    }

    ready.sort((a, b) => a.match.roundNumber - b.match.roundNumber || a.match.positionInRound - b.match.positionInRound)
    waiting.sort((a, b) => a.match.roundNumber - b.match.roundNumber || a.match.positionInRound - b.match.positionInRound)

    return {
      completedMatches: completed,
      totalMatches: total,
      readyMatches: ready,
      waitingMatches: waiting
    }
  }, [bracket.matches, bracket.totalRounds, playersById, t])

  const progressValue = totalMatches > 0 ? Math.round((completedMatches / totalMatches) * 100) : 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon icon={ICONS.podium} width={18} height={18} />
          {t('knockout.standings.title', 'Current tournament progress')}
        </CardTitle>
        <CardDescription>
          {t('knockout.standings.subtitle', 'Track remaining matches and upcoming rounds in the bracket.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm font-medium text-muted-foreground">
            <span>{t('knockout.standings.completed', 'Completed matches')}</span>
            <span>
              {completedMatches} / {totalMatches}
            </span>
          </div>
          <Progress value={progressValue} className="h-2" />
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Icon icon={ICONS.calendar} width={16} height={16} className="text-primary" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t('knockout.standings.nextMatches', 'Next matches')}
            </h3>
          </div>

          {readyMatches.length === 0 ? (
            <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/40 p-4 text-sm text-muted-foreground">
              {t('knockout.standings.noUpcoming', 'Waiting for previous rounds to finish before the next matches are scheduled.')}
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {readyMatches.slice(0, 4).map(({ match, roundLabel, playerAName, playerBName }) => (
                <div key={match.id} className="rounded-lg border bg-card p-4 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {roundLabel}
                  </div>
                  <div className="mt-2 flex flex-col gap-1 text-sm font-medium">
                    <span>{playerAName ?? t('common.unknownPlayer')}</span>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {t('knockout.standings.vs', 'vs')}
                    </div>
                    <span>{playerBName ?? t('common.unknownPlayer')}</span>
                  </div>
                  <Badge variant="secondary" className="mt-3 w-fit">
                    {t('knockout.standings.ready', 'Ready to play')}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        {waitingMatches.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Icon icon={ICONS.clock} width={16} height={16} className="text-warning" />
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t('knockout.standings.waitingMatches', 'Awaiting qualification')}
              </h3>
            </div>
            <div className="space-y-2">
              {waitingMatches.slice(0, 3).map(({ match, roundLabel, missingSide }) => (
                <div key={match.id} className="rounded-lg border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
                  <div className="font-medium text-foreground">{roundLabel}</div>
                  <div>
                    {missingSide === 'BOTH'
                      ? t('knockout.standings.waitingBoth', 'Waiting for both participants to be decided.')
                      : missingSide === 'A'
                        ? t('knockout.standings.waitingPlayerA', 'Waiting for the winner that feeds into side A.')
                        : t('knockout.standings.waitingPlayerB', 'Waiting for the winner that feeds into side B.')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
